import * as React from 'react';
import { GoogleMap, Marker, useLoadScript, Autocomplete } from '@react-google-maps/api';
import { Modal } from 'antd';
import { CalendarDays, ChevronDown, Crosshair, LocateFixed, Search, Upload } from 'lucide-react';
import { Formik, FormikHelpers, Form } from 'formik';
import { showGlobalModal } from 'core/components/GlobalModal';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { apiFileUpload } from 'core/utils/fileUpload/api';
import httpclient from 'libs/Axios';
import { DigitalMarketingBreadcrumb } from 'features/DigitalMarketing/components/DigitalMarketingBreadcrumb';
import { ImagePreviewModal } from 'features/DigitalMarketing/components/ImagePreviewModal';
import { FieldLabel } from 'features/DigitalMarketing/components/FormControls';
import { DMFormField } from 'features/DigitalMarketing/components/DMFormField';
import { DMSelectField } from 'features/DigitalMarketing/components/DMSelectField';
import { DMFormActions } from 'features/DigitalMarketing/components/DMFormActions';
import { DMDiv, DMButton, DMImage, DMInput, DMText, DMLabel } from 'features/DigitalMarketing/components/Primitives';
import { editWebsiteContentLabelClassName } from 'features/DigitalMarketing/constants/formStyles';
import { EDIT_WEBSITE_CONTENT_BASE_PATH } from 'features/DigitalMarketing/routes/editWebsiteContentPaths';
import { extractUploadedUrl, getApiErrorMessage } from 'features/DigitalMarketing/utils/apiResponse';
import { ReactComponent as LoadingSpinner } from 'assets/images/DigitalMarketing/loading-spinner.svg';
import { getAppEnv } from 'core/utils/runtimeConfig';

type LocationValue = {
    lat: number;
    lng: number;
    address: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    place_id?: string;
};

type MetadataOptions = {
    InstitutionType: string[];
    BoardType: string[];
    Instruction_Medium: string[];
    GradeLevels: string[];
    AddonPrograms: string[];
};

type InstitutionDetailsValues = {
    institutionName: string;
    institutionType: string;
    boardType: string;
    addOnPrograms: string[];
    totalFacultyCount: string;
    schoolLogo: string;
    contactNumber: string;
    foundingDate: string;
    mediumOfInstruction: string[];
    gradeClassLevels: string[];
    totalStudentCount: string;
    addressLocation: string;
    mapLocation: LocationValue | null;
    brandColors: string;
    emailAddress: string;
};

const initialValues: InstitutionDetailsValues = {
    institutionName: '',
    institutionType: '',
    boardType: '',
    addOnPrograms: [],
    totalFacultyCount: '',
    schoolLogo: '',
    contactNumber: '',
    foundingDate: '',
    mediumOfInstruction: [],
    gradeClassLevels: [],
    totalStudentCount: '',
    addressLocation: '',
    mapLocation: null,
    brandColors: '',
    emailAddress: '',
};

type InstituteWebDetailsResponse = {
    institution_name?: string;
    institution_type?: string;
    board_type?: string;
    addon_programs?: string[];
    total_faculty_count?: number | string;
    school_logo?: string;
    contact_number?: string;
    founding_date?: string;
    medium_of_instruction?: string[];
    grade_levels?: string[];
    total_student_count?: number | string;
    address?: string;
    brand_colors?: string;
    brand_color?: string;
    email_address?: string;
    latitude?: number | string;
    longitude?: number | string;
    lat?: number | string;
    lng?: number | string;
    map_location?: Partial<LocationValue> | null;
    mapLocation?: Partial<LocationValue> | null;
};

const parseCoordinate = (value: unknown) => {
    const coordinate = typeof value === 'number' ? value : Number(value);

    return Number.isFinite(coordinate) ? coordinate : null;
};

const mapApiLocationToLocationValue = (data?: InstituteWebDetailsResponse | null): LocationValue | null => {
    const mapLocation = data?.map_location || data?.mapLocation;
    const lat = parseCoordinate(mapLocation?.lat ?? data?.latitude ?? data?.lat);
    const lng = parseCoordinate(mapLocation?.lng ?? data?.longitude ?? data?.lng);
    const address = mapLocation?.address || data?.address || '';

    if (lat === null || lng === null) {
        return null;
    }

    return {
        lat,
        lng,
        address,
        city: mapLocation?.city,
        state: mapLocation?.state,
        postalCode: mapLocation?.postalCode,
        country: mapLocation?.country,
        place_id: mapLocation?.place_id,
    };
};

const mapInstituteDetailsToFormValues = (data?: InstituteWebDetailsResponse | null): InstitutionDetailsValues => {
    const addonPrograms = data?.addon_programs;
    const mediumOfInstruction = data?.medium_of_instruction;
    const gradeLevels = data?.grade_levels;
    const mapLocation = mapApiLocationToLocationValue(data);

    return {
        ...initialValues,
        institutionName: data?.institution_name || '',
        institutionType: data?.institution_type || '',
        boardType: data?.board_type || '',
        addOnPrograms: Array.isArray(addonPrograms) ? addonPrograms : [],
        totalFacultyCount: data?.total_faculty_count !== undefined ? String(data.total_faculty_count) : '',
        schoolLogo: data?.school_logo || '',
        contactNumber: data?.contact_number || '',
        foundingDate: data?.founding_date || '',
        mediumOfInstruction: Array.isArray(mediumOfInstruction) ? mediumOfInstruction : [],
        gradeClassLevels: Array.isArray(gradeLevels) ? gradeLevels : [],
        totalStudentCount: data?.total_student_count !== undefined ? String(data.total_student_count) : '',
        addressLocation: data?.address || '',
        mapLocation,
        brandColors: data?.brand_colors || data?.brand_color || '',
        emailAddress: data?.email_address || '',
    };
};

const buildInstituteDetailsPayload = (values: InstitutionDetailsValues) => ({
    institution_name: values.institutionName.trim(),
    institution_type: values.institutionType,
    board_type: values.boardType,
    addon_programs: values.addOnPrograms,
    total_faculty_count: Number(values.totalFacultyCount) || 0,
    school_logo: values.schoolLogo,
    contact_number: values.contactNumber.trim(),
    founding_date: values.foundingDate,
    medium_of_instruction: values.mediumOfInstruction,
    grade_levels: values.gradeClassLevels,
    total_student_count: Number(values.totalStudentCount) || 0,
    address: values.addressLocation.trim(),
    latitude: values.mapLocation?.lat,
    longitude: values.mapLocation?.lng,
    map_location: values.mapLocation,
    brand_colors: values.brandColors,
    email_address: values.emailAddress.trim(),
});

const defaultMetadataOptions: MetadataOptions = {
    InstitutionType: ['School', 'College', 'Pre-school', 'Coaching Institute'],
    BoardType: ['CBSE', 'ICSE', 'State Board', 'IB', 'Cambridge'],
    Instruction_Medium: ['English', 'Hindi', 'Regional Language', 'Bilingual'],
    GradeLevels: ['Pre-primary', 'Primary', 'Middle School', 'High School', 'Senior Secondary'],
    AddonPrograms: ['Sports', 'Arts', 'STEM Lab', 'Robotics', 'Language Lab'],
};

const normalizeOptionList = (value: any, fallback: string[]) => {
    if (!Array.isArray(value)) {
        return fallback;
    }

    const options = value
        .map(item => (
            typeof item === 'string'
                ? item
                : item?.label || item?.value || item?.NAME || item?.name || ''
        ))
        .filter(Boolean);

    return options.length ? options : fallback;
};

const normalizeMetadataOptions = (data: any): MetadataOptions => ({
    InstitutionType: normalizeOptionList(data?.InstitutionType || data?.institutionType, defaultMetadataOptions.InstitutionType),
    BoardType: normalizeOptionList(data?.BoardType || data?.boardType, defaultMetadataOptions.BoardType),
    Instruction_Medium: normalizeOptionList(data?.Instruction_Medium || data?.instructionMedium, defaultMetadataOptions.Instruction_Medium),
    GradeLevels: normalizeOptionList(data?.GradeLevels || data?.gradeLevels, defaultMetadataOptions.GradeLevels),
    AddonPrograms: normalizeOptionList(data?.AddonPrograms || data?.addonPrograms, defaultMetadataOptions.AddonPrograms),
});

const formatLocationAddress = (location: LocationValue | null) => {
    if (!location) {
        return '';
    }

    return Array.from(new Set([
        location.address,
        location.city,
        location.state,
        location.postalCode,
        location.country,
    ].filter(Boolean))).join(', ');
};



type MultiSelectFieldProps = {
    label: string;
    value: string[];
    placeholder: string;
    options: string[];
    onChange: (nextValue: string[]) => void;
};

const MultiSelectField = ({
    label,
    value,
    placeholder,
    options,
    onChange,
}: MultiSelectFieldProps) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const fieldRef = React.useRef<HTMLDivElement>(null);
    const displayValue = value.length ? value.join(', ') : placeholder;

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (fieldRef.current && !fieldRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleToggleOption = (option: string) => {
        onChange(value.includes(option)
            ? value.filter(item => item !== option)
            : [...value, option]);
    };

    return (
        <DMDiv className="relative" ref={fieldRef}>
            <FieldLabel className={editWebsiteContentLabelClassName}>
                {label}
            </FieldLabel>
            <DMButton
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex h-[7.4vh] w-full items-center justify-between gap-[0.8vw] rounded-[10px] border border-gray-300 bg-white px-[1.1vw] text-[1.95vh] font-medium outline-none hover:border-primary-main"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <DMText as="span" className={`truncate ${value.length ? 'text-gray-950' : 'text-gray-400'}`}>{displayValue}</DMText>
                <ChevronDown className="h-[2.2vh] w-[2.2vh] text-gray-950" strokeWidth={2} />
            </DMButton>
            {isOpen && (
                <DMDiv className="absolute left-0 right-0 top-[calc(100%+0.6vh)] z-30 max-h-[26vh] overflow-y-auto rounded-[10px] border border-gray-200 bg-white py-[0.6vh] shadow-[0_14px_35px_rgba(15,23,42,0.16)]">
                    {options.map(option => (
                        <DMText as="label" key={option} className="flex cursor-pointer items-center gap-[0.7vw] px-[1vw] py-[1vh] text-[1.65vh] font-medium text-gray-800 hover:bg-gray-50">
                            <DMInput
                                type="checkbox"
                                checked={value.includes(option)}
                                onChange={() => handleToggleOption(option)}
                                className="h-[1.8vh] w-[1.8vh] accent-primary-main"
                            />
                            <DMText as="span" className="truncate">{option}</DMText>
                        </DMText>
                    ))}
                </DMDiv>
            )}
        </DMDiv>
    );
};

type ActionFieldProps = {
    label: string;
    value: string;
    placeholder: string;
    icon: React.ReactNode;
    onClick?: () => void;
};

const ActionField = ({ label, value, placeholder, icon, onClick }: ActionFieldProps) => (
    <DMDiv>
        <FieldLabel className={editWebsiteContentLabelClassName}>
            {label}
        </FieldLabel>
        <DMButton
            type="button"
            onClick={onClick}
            className="flex h-[7.4vh] w-full min-w-0 items-center justify-between gap-[0.8vw] rounded-[10px] border border-gray-300 bg-white px-[1.1vw] text-left text-[1.95vh] font-medium text-gray-400 outline-none hover:border-primary-main"
        >
            <DMText as="span" className={`min-w-0 flex-1 truncate ${value ? 'text-gray-950' : 'text-gray-400'}`} title={value || placeholder}>
                {value || placeholder}
            </DMText>
            <DMText as="span" className="shrink-0 text-gray-950">{icon}</DMText>
        </DMButton>
    </DMDiv>
);

// type ColorPickerFieldProps = {
//     label: string;
//     value: string;
//     placeholder: string;
//     onChange: (value: string) => void;
// };

// const normalizeHexColor = (value: string) => (
//     /^#[0-9A-Fa-f]{6}$/.test(value) ? value : '#4F46E5'
// );

// const ColorPickerField = ({ label, value, placeholder, onChange }: ColorPickerFieldProps) => {
//     const pickerValue = normalizeHexColor(value);

//     return (
//         <div>
//             <FieldLabel className={editWebsiteContentLabelClassName}>
//                 {label}
//             </FieldLabel>
//             <div className="flex h-[7.4vh] w-full items-center gap-[0.85vw] rounded-[10px] border border-gray-300 bg-white px-[1.1vw] outline-none focus-within:border-primary-main">
//                 <input
//                     type="text"
//                     value={value}
//                     onChange={event => onChange(event.target.value)}
//                     placeholder={placeholder}
//                     className="min-w-0 flex-1 bg-transparent text-[1.95vh] font-medium text-gray-950 outline-none placeholder:text-gray-400"
//                 />
//                 <label
//                     className="relative h-[3.4vh] w-[3.4vh] shrink-0 cursor-pointer overflow-hidden rounded-full border border-gray-300"
//                     style={{ backgroundColor: pickerValue }}
//                     aria-label={label}
//                 >
//                     <input
//                         type="color"
//                         value={pickerValue}
//                         onChange={event => onChange(event.target.value)}
//                         className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
//                     />
//                 </label>
//             </div>
//         </div>
//     );
// };

type LocationModalProps = {
    isOpen: boolean;
    initialLocation: LocationValue | null;
    initialAddress: string;
    onClose: () => void;
    onSave: (location: LocationValue | null) => void;
};

const libraries: ("places")[] = ['places'];

const LocationModal = ({ isOpen, initialLocation, initialAddress, onClose, onSave }: LocationModalProps) => {
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: getAppEnv('REACT_APP_GOOGLE_API_KEY') || '', libraries
    });
    const autocompleteRef = React.useRef<google.maps.places.Autocomplete | null>(null);
    const [currentLocation, setCurrentLocation] = React.useState<LocationValue | null>(initialLocation);
    const [searchText, setSearchText] = React.useState('');
    const [isLocating, setIsLocating] = React.useState(false);
    const hasGeocodedInitialAddressRef = React.useRef(false);

    const handlePlaceChanged = () => {
        const place = autocompleteRef.current?.getPlace();

        if (place?.geometry?.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();

            // Re-use your existing address extraction logic
            const addressParts = extractAddressParts(place as any);

            setCurrentLocation({
                lat,
                lng,
                address: place.formatted_address || '',
                place_id: place.place_id,
                ...addressParts,
            });
            setSearchText(place.formatted_address || ''); // Update input text
        }
    };

    React.useEffect(() => {
        if (isOpen) {
            setCurrentLocation(initialLocation);
            setSearchText(initialLocation?.address || initialAddress || '');
            hasGeocodedInitialAddressRef.current = false;
        }
    }, [initialAddress, initialLocation, isOpen]);

    const extractAddressParts = (result: google.maps.GeocoderResult) => {
        let city = '';
        let state = '';
        let postalCode = '';
        let country = '';

        result.address_components.forEach(component => {
            if (component.types.includes('postal_code')) {
                postalCode = component.long_name;
            }
            if (component.types.includes('country')) {
                country = component.long_name;
            }
            if (component.types.includes('administrative_area_level_3') || component.types.includes('locality')) {
                city = component.long_name;
            }
            if (component.types.includes('administrative_area_level_1')) {
                state = component.long_name;
            }
        });

        return { city, state, postalCode, country };
    };

    const handleSelectPosition = (lat: number, lng: number) => {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
                const addressParts = extractAddressParts(results[0]);
                setCurrentLocation({
                    lat,
                    lng,
                    address: results[0].formatted_address,
                    place_id: results[0].place_id,
                    ...addressParts,
                });
            } else {
                setCurrentLocation({ lat, lng, address: `${lat.toFixed(6)}, ${lng.toFixed(6)}` });
            }
        });
    };

    const handleSearch = () => {
        if (!searchText.trim() || !window.google?.maps) {
            return;
        }

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: searchText }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
                const lat = results[0].geometry.location.lat();
                const lng = results[0].geometry.location.lng();
                const addressParts = extractAddressParts(results[0]);
                setCurrentLocation({
                    lat,
                    lng,
                    address: results[0].formatted_address,
                    place_id: results[0].place_id,
                    ...addressParts,
                });
            }
        });
    };

    React.useEffect(() => {
        if (
            !isOpen
            || !isLoaded
            || currentLocation
            || hasGeocodedInitialAddressRef.current
            || !initialAddress.trim()
            || !window.google?.maps
        ) {
            return;
        }

        hasGeocodedInitialAddressRef.current = true;
        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode({ address: initialAddress }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
                const lat = results[0].geometry.location.lat();
                const lng = results[0].geometry.location.lng();
                const addressParts = extractAddressParts(results[0]);

                setCurrentLocation({
                    lat,
                    lng,
                    address: results[0].formatted_address,
                    place_id: results[0].place_id,
                    ...addressParts,
                });
                setSearchText(results[0].formatted_address);
            }
        });
    }, [currentLocation, initialAddress, isLoaded, isOpen]);

    const handleUseCurrentLocation = () => {
        if (!navigator.geolocation) {
            showGlobalModal('Geolocation is not supported in this browser', 'warning');
            return;
        }

        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(
            position => {
                setIsLocating(false);
                handleSelectPosition(position.coords.latitude, position.coords.longitude);
            },
            () => {
                setIsLocating(false);
                showGlobalModal('Unable to get your location. Please check browser permissions.', 'warning');
            },
        );
    };

    if (!isOpen) {
        return null;
    }

    const center = currentLocation
        ? { lat: currentLocation.lat, lng: currentLocation.lng }
        : { lat: 18.52043, lng: 73.856743 };

    return (
        <Modal
            open={isOpen}
            onCancel={onClose}
            centered
            closable={false}
            footer={null}
            width="38vw"
            styles={{
                content: { padding: 0, overflow: 'hidden', borderRadius: '15px' },
                body: { padding: 0, margin: 0 },
            }}
        >
            <DMDiv className="min-h-[84vh] w-full">
                <DMDiv className="relative h-[68vh] w-full shrink-0 bg-gray-100">
                    {loadError ? (
                        <DMDiv className="flex h-full items-center justify-center text-[1.8vh] font-medium text-red-500">Unable to load Google Maps.</DMDiv>
                    ) : !isLoaded ? (
                        <DMDiv className="flex h-full items-center justify-center text-[1.8vh] font-medium text-gray-500">Loading map...</DMDiv>
                    ) : (
                        <>
                            <GoogleMap
                                mapContainerClassName="h-full w-full"
                                center={center}
                                zoom={currentLocation ? 16 : 6}
                                onClick={event => {
                                    if (event.latLng) {
                                        handleSelectPosition(event.latLng.lat(), event.latLng.lng());
                                    }
                                }}
                                options={{
                                    disableDefaultUI: true,
                                    clickableIcons: true,
                                    fullscreenControl: false,
                                    draggable: true,
                                    zoomControl: false,
                                    scrollwheel: true,
                                    disableDoubleClickZoom: false,
                                    keyboardShortcuts: true,
                                }}
                            >
                                {currentLocation && <Marker position={{ lat: currentLocation.lat, lng: currentLocation.lng }} />}
                            </GoogleMap>
                            <DMDiv className="absolute left-4 right-4 top-4 z-10 flex items-center justify-between gap-3">
                                <DMDiv className="box-border flex h-[4.5vh] flex-1 items-center rounded-[5px] border border-[#9d9d9d] bg-white px-2 shadow-[0_4px_4px_0_rgba(0,0,0,0.25)]">
                                    <Search className="h-[2.2vh] w-[2.2vh] shrink-0 cursor-pointer text-black" strokeWidth={2} onClick={handleSearch} />
                                    <Autocomplete
                                        onLoad={autocomplete => (autocompleteRef.current = autocomplete)}
                                        onPlaceChanged={handlePlaceChanged}
                                        className="h-full flex-1"
                                    >
                                        <DMInput
                                            type="text"
                                            value={searchText}
                                            onChange={event => setSearchText(event.target.value)}
                                            placeholder="Search..."
                                            className="h-full w-full overflow-hidden truncate bg-white px-2 font-inter text-[2.2vh] font-normal text-black outline-none placeholder:text-black"
                                        />
                                    </Autocomplete>
                                </DMDiv>
                                <DMButton
                                    type="button"
                                    onClick={handleUseCurrentLocation}
                                    disabled={isLocating}
                                    className="flex h-[4.5vh] w-[40%] items-center justify-center gap-1 whitespace-nowrap rounded-[5px] border border-gray-400 bg-white p-1 py-2.5 text-xs font-semibold text-black shadow-[0_4px_4px_0_rgba(0,0,0,0.25)] transition-colors hover:bg-gray-50 disabled:opacity-60"
                                >
                                    {isLocating ? (
                                        <>
                                            <LoadingSpinner className="h-3 w-3 animate-spin text-blue-700" aria-hidden="true" />
                                            Getting Location...
                                        </>
                                    ) : (
                                        <>
                                            <Crosshair className="h-3.5 w-3.5 text-black" strokeWidth={2.5} />
                                            <DMText as="span" className="font-inter text-[2.2vh] font-normal">Locate me</DMText>
                                        </>
                                    )}
                                </DMButton>
                            </DMDiv>
                        </>
                    )}
                </DMDiv>

                <DMDiv className="flex h-[16vh] flex-col justify-center gap-3 bg-white px-6 py-4">
                    <DMText as="p" className="truncate font-inter text-[1.8vh] font-normal text-gray-700" title={currentLocation?.address || searchText}>
                        {currentLocation?.address || searchText || 'Search or click on the map to choose a location'}
                    </DMText>
                    <DMDiv className="flex gap-3">
                        <DMButton
                            type="button"
                            onClick={() => onSave(currentLocation)}
                            className="flex h-[5vh] flex-1 items-center justify-center gap-2 rounded-[6px] border-none bg-[#3b49df] font-inter text-[2.1vh] font-normal text-white outline-none transition-colors hover:bg-blue-700 focus:ring-4 focus:ring-blue-300"
                        >
                            Next
                        </DMButton>
                        <DMButton
                            type="button"
                            onClick={onClose}
                            className="flex h-[5vh] flex-1 items-center justify-center gap-2 rounded-[10px] border border-gray-400 bg-white font-inter text-[2.1vh] font-normal text-black outline-none transition-colors hover:bg-gray-50"
                        >
                            Cancel
                        </DMButton>
                    </DMDiv>
                </DMDiv>
            </DMDiv>
        </Modal>
    );
};

export function InstitutionDetailsForm() {
    const authData = useAuth();
    const [metadataOptions, setMetadataOptions] = React.useState<MetadataOptions>(defaultMetadataOptions);
    const [formInitialValues, setFormInitialValues] = React.useState<InstitutionDetailsValues>(initialValues);
    const [isLocationModalOpen, setIsLocationModalOpen] = React.useState(false);
    const [isFetchingDetails, setIsFetchingDetails] = React.useState(false);
    const [isUploadingLogo, setIsUploadingLogo] = React.useState(false);
    const [previewLogoUrl, setPreviewLogoUrl] = React.useState('');

    React.useEffect(() => {
        const fetchMetadata = async () => {
            try {
                const response = await httpclient().get('/master/onboarding/metadata');
                setMetadataOptions(normalizeMetadataOptions(response?.data?.data));
            } catch (error: any) {
                showGlobalModal(getApiErrorMessage(error, 'Unable to fetch institution dropdown options'), 'warning');
                setMetadataOptions(defaultMetadataOptions);
            }
        };

        fetchMetadata();
    }, []);

    React.useEffect(() => {
        const fetchInstituteDetails = async () => {
            setIsFetchingDetails(true);

            try {
                const response = await httpclient().get('/master/viewInstituteWebDetails');
                const nextValues = mapInstituteDetailsToFormValues(response?.data?.data);
                setFormInitialValues(nextValues);
            } catch (error: any) {
                showGlobalModal(getApiErrorMessage(error, 'Unable to fetch institution details'), 'warning');
            } finally {
                setIsFetchingDetails(false);
            }
        };

        fetchInstituteDetails();
    }, []);

    const handleUploadLogo = async (
        file: File,
        setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    ) => {
        if (!file.type.startsWith('image/')) {
            showGlobalModal('Only image files are allowed', 'error');
            return;
        }

        if (file.size > 15 * 1024 * 1024) {
            showGlobalModal('File size exceeds 15MB', 'error');
            return;
        }

        const localPreviewUrl = URL.createObjectURL(file);
        setFieldValue('schoolLogo', localPreviewUrl);
        setIsUploadingLogo(true);

        try {
            const formData = new FormData();
            formData.append('dest', 'WEBSITE');
            formData.append('branch_id', authData?.BRANCH_ID as any);
            formData.append('database', authData?.DATABASE as any);
            formData.append('files', file);

            const response = await apiFileUpload(formData, 'upload', '', '', true, false);
            const logoUrl = extractUploadedUrl(response);

            if (logoUrl) {
                setFieldValue('schoolLogo', logoUrl);
            } else {
                setFieldValue('schoolLogo', '');
                showGlobalModal('Unable to read uploaded logo URL', 'error');
            }
        } catch (error: any) {
            setFieldValue('schoolLogo', '');
            showGlobalModal(getApiErrorMessage(error, 'Failed to upload logo'), 'error');
        } finally {
            setIsUploadingLogo(false);
        }
    };

    const handleSubmit = async (
        values: InstitutionDetailsValues,
        formikHelpers: FormikHelpers<InstitutionDetailsValues>,
    ) => {
        if (!values.institutionName) {
            showGlobalModal('Institution name is required', 'warning');
            formikHelpers.setSubmitting(false);
            return;
        }

        try {
            const payload = buildInstituteDetailsPayload(values);
            const response = await httpclient().post('/master/upsertInstituteWebDetails', payload);
            const savedValues = mapInstituteDetailsToFormValues(payload);

            setFormInitialValues(savedValues);
            formikHelpers.resetForm({ values: savedValues });
            showGlobalModal(response?.data?.message || 'Institution details saved successfully');
        } catch (error: any) {
            showGlobalModal(getApiErrorMessage(error, 'Unable to save institution details'), 'error');
        } finally {
            formikHelpers.setSubmitting(false);
        }
    };

    return (
        <DMDiv as="main" className="flex h-full min-h-0 flex-col overflow-hidden bg-white px-[2.5vw] py-[2.2vh] font-inter text-gray-950">
            <DMDiv className="mb-[4vh] shrink-0">
                <DigitalMarketingBreadcrumb
                    className="mb-[1.6vh]"
                    items={[
                        { label: 'Edit Website Content', path: EDIT_WEBSITE_CONTENT_BASE_PATH },
                        { label: 'Institution Details' },
                    ]}
                />
                <DMText as="h1" className="text-[3.8vh] font-bold leading-none text-gray-950">Manage Institution Details</DMText>
                <DMText as="p" className="mt-[1vh] text-[1.8vh] font-medium leading-tight text-gray-600">
                    Keep your Institution&apos;s information accurate and up to date.
                </DMText>
            </DMDiv>

            <Formik initialValues={formInitialValues} enableReinitialize onSubmit={handleSubmit}>
                {({ values, handleChange, handleSubmit, setFieldValue, isSubmitting }) => (
                    <Form className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        {isFetchingDetails && (
                            <DMDiv className="mb-[1.8vh] rounded-[8px] border border-gray-200 bg-gray-50 px-[1vw] py-[1vh] text-[1.65vh] font-medium text-gray-600">
                                Loading institution details...
                            </DMDiv>
                        )}
                        <DMDiv className="grid shrink-0 grid-cols-3 gap-x-[2.4vw] gap-y-[2.6vh]">
                            <DMFormField label="Institution Name" name="institutionName" placeholder="Enter Institute Name here" value={values.institutionName} onChange={handleChange} />
                            <DMSelectField label="Institution Type" name="institutionType" placeholder="Select type" value={values.institutionType} onChange={handleChange} options={metadataOptions.InstitutionType} />
                            <DMSelectField label="Board Type" name="boardType" placeholder="Select board type" value={values.boardType} onChange={handleChange} options={metadataOptions.BoardType} />

                            <MultiSelectField label="Add-on Programs" placeholder="Select add-on programs" value={values.addOnPrograms} onChange={value => setFieldValue('addOnPrograms', value)} options={metadataOptions.AddonPrograms} />
                            <DMFormField label="Total Faculty Count" name="totalFacultyCount" placeholder="Enter faculty count" value={values.totalFacultyCount} onChange={handleChange} type="number" />
                            <DMDiv>
                                <FieldLabel className={editWebsiteContentLabelClassName}>
                                    School Logo
                                </FieldLabel>
                                <DMDiv className="flex h-[7.4vh] w-full overflow-hidden rounded-[10px] border border-gray-300 bg-white text-[1.95vh] font-medium text-gray-400 outline-none focus-within:border-primary-main hover:border-primary-main">
                                    <DMLabel className={`flex min-w-0 flex-1 items-center justify-between px-[1.1vw] ${isUploadingLogo ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                                        <DMText as="span" className={`min-w-0 flex-1 truncate pr-[1vw] ${values.schoolLogo ? 'text-gray-950' : 'text-gray-400'}`}>
                                            {isUploadingLogo ? 'Uploading...' : values.schoolLogo || 'Upload logo'}
                                        </DMText>
                                        {!values.schoolLogo && (
                                            <Upload className="h-[2.2vh] w-[2.2vh] shrink-0 text-gray-950" strokeWidth={2} />
                                        )}
                                        <DMInput
                                            type="file"
                                            accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                                            className="hidden"
                                            disabled={isUploadingLogo}
                                            onChange={event => {
                                                const file = event.target.files?.[0];
                                                if (file) {
                                                    handleUploadLogo(file, setFieldValue);
                                                }
                                                event.currentTarget.value = '';
                                            }}
                                        />
                                    </DMLabel>
                                    {values.schoolLogo && (
                                        <DMButton
                                            type="button"
                                            onClick={() => setPreviewLogoUrl(values.schoolLogo)}
                                            className="m-[1vh] flex h-[5.4vh] w-[5.4vh] shrink-0 items-center justify-center overflow-hidden rounded-[8px] border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-main focus:ring-offset-2"
                                            aria-label="Preview school logo"
                                        >
                                            <DMImage src={values.schoolLogo} alt="School logo" className="h-full w-full object-contain" />
                                        </DMButton>
                                    )}
                                </DMDiv>
                            </DMDiv>

                            <DMFormField label="Contact Number" name="contactNumber" placeholder="Enter Contact Number" value={values.contactNumber} onChange={handleChange} />
                            <DMFormField label="Founding Date" name="foundingDate" placeholder="dd/mm/yyyy" value={values.foundingDate} onChange={handleChange} icon={<CalendarDays className="h-[2vh] w-[2vh]" strokeWidth={2} />} />
                            <MultiSelectField label="Medium of Instruction" placeholder="Select Medium" value={values.mediumOfInstruction} onChange={value => setFieldValue('mediumOfInstruction', value)} options={metadataOptions.Instruction_Medium} />

                            <MultiSelectField label="Grade/Class Levels" placeholder="Select grade/class" value={values.gradeClassLevels} onChange={value => setFieldValue('gradeClassLevels', value)} options={metadataOptions.GradeLevels} />
                            <DMFormField label="Total Student Count" name="totalStudentCount" placeholder="Enter student count" value={values.totalStudentCount} onChange={handleChange} type="number" />
                            <ActionField
                                label="Address/Location"
                                value={values.addressLocation}
                                placeholder="Enter location manually or via map"
                                icon={<LocateFixed className="h-[2vh] w-[2vh]" strokeWidth={2} />}
                                onClick={() => setIsLocationModalOpen(true)}
                            />

                            {/* <ColorPickerField
                                label="Brand Colors"
                                value={values.brandColors}
                                placeholder="#4F46E5"
                                onChange={value => setFieldValue('brandColors', value)}
                            /> */}
                            <DMFormField label="Email Address" name="emailAddress" placeholder="Enter Email Address" value={values.emailAddress} onChange={handleChange} type="email" />
                        </DMDiv>

                        <DMFormActions
                            isSubmitting={isSubmitting}
                            isDisabled={isFetchingDetails || isUploadingLogo}
                            submitLabel="Save Changes"
                            submittingLabel="Saving..."
                            showReset={false}
                        />
                        <LocationModal
                            isOpen={isLocationModalOpen}
                            initialLocation={values.mapLocation}
                            initialAddress={values.addressLocation}
                            onClose={() => setIsLocationModalOpen(false)}
                            onSave={location => {
                                setFieldValue('mapLocation', location);
                                setFieldValue('addressLocation', formatLocationAddress(location));
                                setIsLocationModalOpen(false);
                            }}
                        />
                        <ImagePreviewModal
                            imageUrl={previewLogoUrl}
                            alt="School logo"
                            onClose={() => setPreviewLogoUrl('')}
                        />
                    </Form>
                )}
            </Formik>
        </DMDiv>
    );
}
